import React, { useEffect, useState } from 'react';
import {
  DueRule,
  Frequency,
  ScheduleCreate,
  ScheduleReturn,
  ScheduleStatus,
  ScheduleUpdate,
} from '../clients/rust-api-client';
import { billingService } from '../clients/ApiClients';
import { Button } from '../common/Catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '../common/Catalyst/dialog';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '../common/Catalyst/fieldset';
import { Input } from '../common/Catalyst/input';
import { Text } from '../common/Catalyst/text';
import ShowAlert from '../ShowAlert';
import {
  billingErrorMessage,
  formatBillingDate,
} from '../utils/billingHelpers';
import { EnumSetting, TextSetting } from './BillingPolicyFields';
import { dueRule, frequency, scheduleStatus } from './enumLabels';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  hoaId: string;
  /** Absent to create; present to edit. */
  schedule?: ScheduleReturn | null;
}

interface Draft {
  name: string;
  amount: string;
  frequency: Frequency;
  first_period_start: string;
  due_rule: DueRule;
  due_day: string;
  due_offset_days: string;
  status: ScheduleStatus;
  effective_to: string;
}

const blank: Draft = {
  name: '',
  amount: '',
  frequency: 'monthly',
  first_period_start: '',
  due_rule: 'day_of_month',
  due_day: '1',
  due_offset_days: '0',
  status: 'draft',
  effective_to: '',
};

const MONEY = /^\d+(\.\d{1,2})?$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Create or edit a dues schedule.
 *
 * **Frequency and the anchor date are create-only.** `ScheduleUpdate`
 * carries neither, and that is deliberate rather than an oversight:
 * both define the period grid every posted run was generated against,
 * so changing them would silently re-describe history. Editing shows
 * them as facts rather than fields.
 */
const ScheduleDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  onSaved,
  hoaId,
  schedule,
}) => {
  const editing = !!schedule;
  const [draft, setDraft] = useState<Draft>(blank);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setFailure(null);
    setDraft(
      schedule
        ? {
            name: schedule.name,
            amount: schedule.amount,
            frequency: schedule.frequency,
            first_period_start: schedule.first_period_start,
            due_rule: schedule.due_rule,
            due_day: schedule.due_day == null ? '' : String(schedule.due_day),
            due_offset_days: String(schedule.due_offset_days),
            status: schedule.status,
            effective_to: schedule.effective_to ?? '',
          }
        : blank
    );
  }, [isOpen, schedule]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]): void => {
    setDraft((d) => ({ ...d, [key]: value }));
    setErrors((e) => {
      if (!e[key]) return e;
      const next = { ...e };
      delete next[key];
      return next;
    });
  };

  const validate = (): Record<string, string> => {
    const found: Record<string, string> = {};
    if (!draft.name.trim()) found.name = 'Give the schedule a name.';
    if (!MONEY.test(draft.amount.trim())) {
      found.amount = 'Enter an amount like 250 or 250.00.';
    }
    if (!editing && !DATE.test(draft.first_period_start)) {
      found.first_period_start = 'Pick the date the first period starts.';
    }
    if (draft.due_rule === 'nth_weekday') {
      found.due_rule = 'This rule is not implemented yet — pick another.';
    }
    if (draft.due_rule === 'day_of_month') {
      const day = Number(draft.due_day);
      if (!/^\d+$/.test(draft.due_day.trim()) || day < 1 || day > 31) {
        found.due_day = 'Enter a day from 1 to 31.';
      }
    }
    if (!/^-?\d+$/.test(draft.due_offset_days.trim())) {
      found.due_offset_days = 'Enter a whole number of days, or 0.';
    }
    if (draft.effective_to && !DATE.test(draft.effective_to)) {
      found.effective_to = 'Use a date, or leave it blank.';
    }
    return found;
  };

  const save = async (): Promise<void> => {
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const dueDay =
      draft.due_rule === 'day_of_month' ? Number(draft.due_day) : null;

    try {
      setSaving(true);
      setFailure(null);
      if (editing && schedule) {
        const update: ScheduleUpdate = {
          name: draft.name.trim(),
          amount: draft.amount.trim(),
          due_rule: draft.due_rule,
          due_day: dueDay,
          due_offset_days: Number(draft.due_offset_days),
          status: draft.status,
        };
        // `effective_to` is a plain option on the way in, so a null is
        // read as "no change" rather than "clear". Sending it when it is
        // blank would do nothing; sending a date sets or moves the end.
        if (draft.effective_to) update.effective_to = draft.effective_to;
        await billingService.updateSchedule({
          scheduleId: schedule.id,
          scheduleUpdate: update,
        });
      } else {
        const create: ScheduleCreate = {
          name: draft.name.trim(),
          amount: draft.amount.trim(),
          frequency: draft.frequency,
          first_period_start: draft.first_period_start,
          due_rule: draft.due_rule,
          due_day: dueDay,
          due_offset_days: Number(draft.due_offset_days),
          status: draft.status,
          effective_to: draft.effective_to || null,
        };
        await billingService.createSchedule({
          hoaId,
          scheduleCreate: create,
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      console.error('Error saving schedule:', err);
      setFailure(billingErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} size="2xl">
      <DialogTitle>
        {editing ? 'Edit dues schedule' : 'New dues schedule'}
      </DialogTitle>
      <DialogBody>
        {failure && (
          <div className="mb-4">
            <ShowAlert
              alertType="error"
              alertTitle="Couldn't save the schedule"
              alertMessage={failure}
              isVisible={!!failure}
              onClose={() => setFailure(null)}
              timeout={12000}
              isInline
            />
          </div>
        )}

        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Name</Label>
              <Description>
                What this appears as on a statement — &ldquo;Monthly
                dues&rdquo;, &ldquo;2027 special assessment&rdquo;.
              </Description>
              <Input
                value={draft.name}
                invalid={!!errors.name}
                onChange={(e) => set('name', e.target.value)}
              />
              {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
            </Field>

            <TextSetting
              label="Amount per period"
              help="The association's standard amount. A home on a different rate gets an override on its own account."
              prefix="$"
              value={draft.amount}
              error={errors.amount}
              onChange={(v) => set('amount', v)}
            />

            {editing ? (
              <Field>
                <Label>Frequency and first period</Label>
                <Description>
                  {frequency.label(draft.frequency)}, anchored to{' '}
                  {formatBillingDate(draft.first_period_start)}.{' '}
                  <strong>Neither can be changed.</strong> They define the
                  period grid every posted run was generated against, so
                  altering them would re-describe what was already charged. A
                  different frequency is a new schedule.
                </Description>
              </Field>
            ) : (
              <>
                <EnumSetting
                  label="Frequency"
                  value={draft.frequency}
                  options={frequency}
                  onChange={(v) => set('frequency', v as Frequency)}
                />
                <Field>
                  <Label>First period starts</Label>
                  <Description>
                    <strong>This anchors the period grid</strong>, it is not
                    &ldquo;when we start billing&rdquo;. Every period steps from
                    this date, so a month-end clamp never drifts the whole
                    schedule forward. A date in the past is a normal way to
                    record when dues began.
                  </Description>
                  <Input
                    type="date"
                    value={draft.first_period_start}
                    invalid={!!errors.first_period_start}
                    onChange={(e) => set('first_period_start', e.target.value)}
                    className="max-w-[14rem]"
                  />
                  {errors.first_period_start && (
                    <ErrorMessage>{errors.first_period_start}</ErrorMessage>
                  )}
                </Field>
              </>
            )}

            <EnumSetting
              label="Due date rule"
              value={draft.due_rule}
              options={dueRule}
              error={errors.due_rule}
              onChange={(v) => set('due_rule', v as DueRule)}
            />

            {draft.due_rule === 'day_of_month' && (
              <TextSetting
                label="Due on day"
                help="1–31. A 31st is clamped to the real last day in shorter months, and the clamp never carries into the next period."
                value={draft.due_day}
                error={errors.due_day}
                onChange={(v) => set('due_day', v)}
              />
            )}

            <TextSetting
              label="Shift the due date by"
              help="Days to move the computed due date. Use a negative number to bill ahead — −10 makes a charge due ten days before the rule's date."
              suffix="days"
              value={draft.due_offset_days}
              error={errors.due_offset_days}
              onChange={(v) => set('due_offset_days', v)}
            />

            <EnumSetting
              label="Status"
              value={draft.status}
              options={scheduleStatus}
              onChange={(v) => set('status', v as ScheduleStatus)}
            />
            <Text className="-mt-4">
              <strong>Activating a schedule bills nothing by itself.</strong>{' '}
              Charges only exist once a billing run is generated and posted,
              which is a separate, deliberate act — so a start date in the past
              will not fire a year of dues when you save this.
            </Text>

            <Field>
              <Label>Ends after</Label>
              <Description>
                The last date this schedule applies. Leave blank for no end.
                {editing && (
                  <>
                    {' '}
                    <strong>An end date cannot be removed once set</strong> — it
                    can only be moved to another date.
                  </>
                )}
              </Description>
              <Input
                type="date"
                value={draft.effective_to}
                invalid={!!errors.effective_to}
                onChange={(e) => set('effective_to', e.target.value)}
                className="max-w-[14rem]"
              />
              {errors.effective_to && (
                <ErrorMessage>{errors.effective_to}</ErrorMessage>
              )}
            </Field>
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button color="sky" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : editing ? 'Save changes' : 'Create schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleDialog;
